// The ground's height windows on the client (server: simulation/src/groundwire.py has the format, groundwin.py who is sent what; design: claude/land-truth-design.md, step 3a phase 2).
//
// The ground is drawn as nested rings of heights centred on the eye (a clipmap, groundClip.ts). The server paints each ring from the land reader's terrain artist and sends the windows of the
// viewer it needs as "GCP" frames; this file is the client's side of the wire:
//
//   * `isGroundFrame` tells a "GCP" frame from a physics frame and from the cloud volume's "CVP" and the world painter's "WCP" frames;
//   * `GroundAssembler` puts the frames of a package together (the streams are the package id's low five bits: 0 to 15 the rings, fine to coarse, 16 the shore window; each keeps its newest
//     package only), inflates the parts as they arrive, and when the package is whole joins the height planes and undoes the row differences.
//
// A window is a lattice of n x n vertices, `dx` apart, with vertex (i, j) at tile (x, y) = (x0 + i dx, y0 + j dx): x east, y north, metres from the south-west corner of the old 128 m tile (the
// frame the whole client works in). Heights are `base + scale * q` metres above the sea (q the 16-bit counts); the class is a byte per vertex (0 sea, 1 coastal flat, 2 gentle, 3 moderate, 4 steep).
const HEAD = 12;
const KIND_BEGIN = 1, KIND_PART = 2;
const STREAM_BITS = 5;
const STREAM_MASK = (1 << STREAM_BITS) - 1;
const MSG_TYPE = 'ground_win';
const MAX_N = 1024;

/** The ring windows' streams: 0 to RINGS - 1 (dx = DX0 * 2^k, RING_N vertices across), then the shore window. Must agree with groundwire.py. */
export const RINGS = 16;
export const RING_N = 128;
export const DX0 = 0.04;
export const SHORE_STREAM = RINGS;
export const SHORE_N = 512;
export const SHORE_K = 6;
export const STREAM_COUNT = RINGS + 1;

/** Cell size of a stream (m). */
export function streamDx(stream: number): number { return DX0 * 2 ** (stream === SHORE_STREAM ? SHORE_K : stream); }
/** Vertices across a stream's windows. */
export function streamN(stream: number): number { return stream === SHORE_STREAM ? SHORE_N : RING_N; }

/** True for one of this module's frames ("GCP", version 1, ..., 0xFF in byte 7, which no physics frame's tick can have). */
export function isGroundFrame(buf: ArrayBuffer): boolean {
  if (buf.byteLength < HEAD) return false;
  const b = new Uint8Array(buf, 0, 8);
  return b[0] === 0x47 && b[1] === 0x43 && b[2] === 0x50 && b[3] === 1 && b[7] === 0xFF;
}

async function inflateZ(bytes: Uint8Array): Promise<Uint8Array> {
  const stream = new Blob([bytes as BlobPart]).stream().pipeThrough(new DecompressionStream('deflate'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

export interface GroundPartSpec { o: number; n: number; st: number; f: 'none' | 'b8' }
export interface GroundMeta {
  type: 'ground_win';
  v: number;
  stream: number;
  key: [number, number, number];
  n: number;
  dx: number;
  /** tile position of vertex (0, 0) */
  x0: number; y0: number;
  base: number; scale: number;
  /** the real level the artist refined from, and the artist's level */
  tl: number; lvl: number;
  bin: { len: { d: number }; parts: GroundPartSpec[] };
}
/** A whole window: counts q (row j = tile y, column i = tile x, n * n), classes, and the meta. */
export interface GroundWindow { meta: GroundMeta; q: Uint16Array; cls: Uint8Array }

/** Height of a window's vertex k (m above the sea): base + scale * q. */
export function windowHeight(w: GroundWindow, k: number): number { return w.meta.base + w.meta.scale * w.q[k]!; }

interface Assembly { id: number; meta: GroundMeta; data: Uint8Array; left: number }

/** Row differences of bytes undone in place: a running sum along each row of `row` bytes, modulo 256. */
function unfilterBytes(a: Uint8Array, row: number): void {
  for (let r = 0; r < a.length; r += row) for (let i = r + 1; i < r + row; i++) a[i] = (a[i]! + a[i - 1]!) & 255;
}

/** Join the two height planes and undo the 16-bit row differences; split the class plane. */
function finish(meta: GroundMeta, data: Uint8Array): GroundWindow {
  const n = meta.n, nn = n * n;
  const q = new Uint16Array(nn);
  for (let j = 0; j < n; j++) {
    let acc = 0;
    for (let i = 0; i < n; i++) {
      const k = j * n + i;
      acc = (acc + (data[k]! | (data[nn + k]! << 8))) & 0xFFFF;
      q[k] = acc;
    }
  }
  return { meta, q, cls: data.slice(2 * nn, 3 * nn) };
}

/** Collects the frames of the newest package of each stream. A begin frame starts a package and drops an unfinished one of the same stream; parts of any other package are ignored. When the
 *  last part of a package is in, `onWindow` gets the window. */
export class GroundAssembler {
  private cur: (Assembly | null)[] = new Array(1 << STREAM_BITS).fill(null);
  /** packages abandoned unfinished (a newer one of the same stream began first), for the console */
  dropped = 0;

  constructor(private readonly onWindow: (w: GroundWindow) => void, private readonly onError?: (e: unknown) => void) {}

  accept(buf: ArrayBuffer): void {
    const v = new DataView(buf);
    const kind = v.getUint8(4), id = v.getUint32(8, true), s = id & STREAM_MASK;
    if (kind === KIND_BEGIN) {
      let meta: GroundMeta;
      try {
        meta = JSON.parse(new TextDecoder().decode(new Uint8Array(buf, 16, v.getUint32(12, true)))) as GroundMeta;
        if (meta.type !== MSG_TYPE || !meta.bin || !Array.isArray(meta.bin.parts)) throw new Error('not a ground window');
        if (!(meta.n > 0 && meta.n <= MAX_N) || meta.bin.len.d !== 3 * meta.n * meta.n || meta.bin.parts.length !== 3 || meta.stream !== s) throw new Error('ground window: the meta does not describe a window');
      } catch (e) { this.cur[s] = null; this.onError?.(e); return; }
      const old = this.cur[s];
      if (old && old.left > 0) this.dropped++;
      this.cur[s] = { id, meta, data: new Uint8Array(meta.bin.len.d), left: meta.bin.parts.length };
      return;
    }
    const a = this.cur[s];
    if (kind !== KIND_PART || !a || a.id !== id) return;
    const spec = a.meta.bin.parts[v.getUint16(12, true)];
    if (!spec) return;
    inflateZ(new Uint8Array(buf, 14)).then(raw => {
      if (this.cur[s] !== a) return;                                       // a newer package of this stream began while this one was inflating
      if (raw.length !== spec.n || spec.o + spec.n > a.data.length) throw new Error(`ground window part @${spec.o}: ${raw.length} bytes, expected ${spec.n}`);
      if (spec.f === 'b8') unfilterBytes(raw, spec.st);
      a.data.set(raw, spec.o);
      if (--a.left === 0) { this.cur[s] = null; this.onWindow(finish(a.meta, a.data)); }
    }).catch(e => { if (this.cur[s] === a) this.cur[s] = null; this.onError?.(e); });
  }
}
